import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDebounce } from '@eika/hooks';
import { IconTypes } from '@eika/icon';
import { ErrorLabel, Label, LabelTypes } from '@eika/label';

import { CustomSelect } from '../../../components/CustomSelect';
import { AccountLabels } from '../../../components/shared/AccountLabels';
import { useSuspendedAccounts } from '../../../fetchHooks/useAccount';
import { useAccountNotificationMutations, useAccountNotifications } from '../../../fetchHooks/useAccountNotifications';
import { useSuspendedFavoriteAccount } from '../../../fetchHooks/useFavoriteAccount';
import { useSuspendedSalaryInfo } from '../../../fetchHooks/useSalaryInfo';
import { Account, AccountNumber, INotificationRule } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { pushTrackIDs } from '../../../utils/gtm/trackIDs/push';
import { ToggleItem } from './ToggleItem';

const SalaryNotificationSettings: React.FC = () => {
    const { pushNotifications: pushNotificationTexts } = useTranslation();
    const label = pushNotificationTexts.toggleLabel.salary;

    const [selectedAccount, setSelectedAccount] = useState<Account>();
    const [showChangeText, setShowChangeText] = useState(false);

    const { data: salaryNotifications = [] } = useAccountNotifications({
        select: (data) => data.filter((notification) => notification.type === 'DEPOSIT_TYPE'),
    });
    const targetNotification = useMemo(
        () => salaryNotifications.find((notification) => notification.transactionType === 'WAGE'),
        [salaryNotifications],
    );

    const { createAccountNotification, deleteAccountNotification, updateAccountNotification } =
        useAccountNotificationMutations();

    const { data: accounts } = useSuspendedAccounts({
        select: (accounts) =>
            accounts.filter((account) => account.rights.paymentFrom && !!account.rights.accessPermission),
    });
    const { data: favoriteAccount } = useSuspendedFavoriteAccount();
    const { data: salaryInfo } = useSuspendedSalaryInfo();

    useEffect(() => {
        if (!selectedAccount) {
            setSelectedAccount(
                accounts.find(
                    (account) =>
                        account.accountNumber.value ===
                        (targetNotification?.accountNo.value ??
                            salaryInfo?.accountNumber.value ??
                            favoriteAccount?.value),
                ) ?? accounts[0],
            );
        }
    }, [targetNotification, selectedAccount, favoriteAccount, salaryInfo, setSelectedAccount, accounts]);

    const debounceReset = useDebounce(() => {
        if (!createAccountNotification.isError) createAccountNotification.reset();
        if (!updateAccountNotification.isError) updateAccountNotification.reset();
        if (!deleteAccountNotification.isError) deleteAccountNotification.reset();
    }, 2000);

    const toggleNotifiaction = useCallback(() => {
        if (!targetNotification) {
            const targetAccount =
                selectedAccount ??
                accounts.find(
                    (account) =>
                        account.accountNumber.value === (salaryInfo?.accountNumber.value ?? favoriteAccount?.value),
                );
            createAccountNotification.mutate({
                transactionType: 'WAGE',
                accountNo: targetAccount?.accountNumber ?? accounts[0].accountNumber,
                type: 'DEPOSIT_TYPE',
            });
        } else deleteAccountNotification.mutate(targetNotification.id);
        debounceReset();
    }, [
        debounceReset,
        selectedAccount,
        targetNotification,
        createAccountNotification,
        deleteAccountNotification,
        salaryInfo,
        favoriteAccount,
        accounts,
    ]);

    const debounceHideChangeText = useDebounce(() => setShowChangeText(false), 2000);

    const triggerChangeOfNotification = useCallback(
        async (targetAccountNumber: AccountNumber) => {
            const payload: INotificationRule = {
                transactionType: 'WAGE',
                accountNo: targetAccountNumber,
                type: 'DEPOSIT_TYPE',
            };
            if (targetNotification) {
                setShowChangeText(true);
                await updateAccountNotification
                    .mutateAsync({
                        ...targetNotification,
                        ...payload,
                    })
                    .catch(() =>
                        setSelectedAccount(
                            accounts.find(
                                (account) =>
                                    account.accountNumber.value ===
                                    (targetNotification?.accountNo.value ??
                                        salaryInfo?.accountNumber.value ??
                                        favoriteAccount?.value),
                            ) ?? accounts[0],
                        ),
                    );
                debounceHideChangeText();
            } else {
                await createAccountNotification.mutateAsync(payload);
            }
            debounceReset();
        },
        [
            debounceHideChangeText,
            debounceReset,
            updateAccountNotification,
            accounts,
            createAccountNotification,
            salaryInfo,
            favoriteAccount,
            targetNotification,
        ],
    );

    const onSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            await triggerChangeOfNotification(selectedAccount?.accountNumber ?? accounts[0].accountNumber);
        },
        [triggerChangeOfNotification, selectedAccount, accounts],
    );

    const statusLabel = useMemo((): { labelMessage: string; labelType: LabelTypes; icon: IconTypes } | null => {
        if (updateAccountNotification.isPending)
            return {
                labelMessage: pushNotificationTexts.salary.tryingToChangeChosenAccount,
                labelType: 'pending',
                icon: IconTypes.TIMEGLASS_24,
            };
        if (updateAccountNotification.isError)
            return {
                labelMessage: pushNotificationTexts.salary.editFailed,
                labelType: 'error',
                icon: IconTypes.KRYSS_24,
            };
        if (updateAccountNotification.isSuccess)
            return {
                labelMessage: pushNotificationTexts.salary.accountChanged,
                labelType: 'success',
                icon: IconTypes.USP_CHECKMARK_24,
            };

        return null;
    }, [updateAccountNotification, pushNotificationTexts.salary]);

    const onChangeAccount = useCallback(
        async (accountNumberString: string) => {
            updateAccountNotification.reset();
            setSelectedAccount(accounts.find((account) => account.accountNumber.value === accountNumberString));
            if (targetNotification) {
                await triggerChangeOfNotification({ value: accountNumberString });
            }
        },
        [setSelectedAccount, updateAccountNotification, accounts, targetNotification, triggerChangeOfNotification],
    );

    const isPending =
        createAccountNotification.isPending ||
        deleteAccountNotification.isPending ||
        updateAccountNotification.isPending;

    return (
        <form className="notifications-settings__salary-notifications" onSubmit={onSubmit}>
            <ToggleItem
                label={pushNotificationTexts.salary.title}
                subLabel={label}
                checked={!!targetNotification}
                isLoading={isPending}
                disabled={isPending}
                onToggle={toggleNotifiaction}
                aria-label={`${label} bryter`}
                data-track-id={pushTrackIDs.toggle.salary}
                data-track-linkname="salary-link"
                asHeader
            >
                {(createAccountNotification.isError || deleteAccountNotification.isError) && (
                    <ErrorLabel text={pushNotificationTexts.errorToggleRule} icon={IconTypes.UTROPSTEGN_24} />
                )}
            </ToggleItem>
            <CustomSelect.Root
                label={
                    <span className="unstyled notifications-settings__salary-notifications__select-label">
                        <span className="semibold notifications-settings__salary-notifications__main-select-label">
                            {pushNotificationTexts.salary.chooseSalaryAccount}
                        </span>
                        {(showChangeText || updateAccountNotification.isError) && !!statusLabel && (
                            <Label
                                icon={statusLabel.icon}
                                type={statusLabel.labelType}
                                text={statusLabel.labelMessage}
                            />
                        )}
                    </span>
                }
                id="salary-notification-account"
                onValueChange={onChangeAccount}
                value={selectedAccount?.accountNumber.value}
            >
                <CustomSelect.Button placeholder={pushNotificationTexts.chooseAccountHere} locked={isPending}>
                    <span className="notifications-settings__salary-notifications__select-content">
                        <span className="from-account-select__list-item-owners">
                            <AccountLabels
                                owners={selectedAccount?.owners ?? []}
                                className="account-button__owner-label"
                            />
                        </span>
                        <span className="semibold">{selectedAccount?.alias}</span>
                        <span>{selectedAccount?.accountNumber.value}</span>
                    </span>
                </CustomSelect.Button>
                <CustomSelect.OptionList>
                    {accounts.map((account) => (
                        <CustomSelect.Option key={account.accountId.value} item={account.accountNumber.value}>
                            <span className="notifications-settings__salary-notifications__select-content">
                                <span className="from-account-select__list-item-owners">
                                    <AccountLabels
                                        owners={account.owners ?? []}
                                        className="account-button__owner-label"
                                    />
                                </span>
                                <span className="semibold">{account.alias}</span>
                                <span>{account.accountNumber.value}</span>
                            </span>
                        </CustomSelect.Option>
                    ))}
                </CustomSelect.OptionList>
            </CustomSelect.Root>
        </form>
    );
};

export default SalaryNotificationSettings;
